import { sb } from '../supabase';
import { signRequestAttachments } from './requestAttachmentUrls';
import { isRequestOfferable } from './requestDistribution';
import { fetchRequestFee, isFeeSettled } from './requestFee';

// One request, everything an admin needs to look at it and decide who sees it.
//
// ── Why the "as the factory sees it" block is built from the SAME columns ───
// get_factory_invite is what /f/<slug> actually reads, and it is a SECURITY
// DEFINER function keyed on an invite slug — there is no invite to key on until
// somebody has been invited, so this cannot call it. It reads the same columns
// that function reads, in the same precedence, which keeps the preview honest
// for the fields it shows. What it deliberately does NOT do is claim to be the
// page: the detail screen links to the real /f/<slug> whenever an invite exists,
// because the only faithful preview of a page is that page.
//
// The brief is the one field with no equivalent here. /f/ shows
// managed_request_briefs.supplier_brief when the AI wrote one; a factory-targeted
// or open request usually has none, and the description is what the factory sees
// instead. Both are fetched so the caller can show whichever exists and say which.

const nf = (v) => (v && v !== 'not_found' ? String(v).trim() : '');

export async function fetchRequestDetail(requestId) {
  const [{ data: req, error }, cust, invites, brief, offers, originals] = await Promise.all([
    sb.from('requests')
      .select('id, request_ref, title_ar, title_en, title_zh, description, description_ar, description_en, description_zh, '
        + 'category, quantity, unit, budget_per_unit, budget_currency, request_kind, sourcing_mode, status, '
        + 'managed_status, response_deadline, reference_image, attachment_paths, attachments_missing, '
        + 'ship_country, ship_city, buyer_id, created_at, last_activity_at, '
        // The trader's terms the supplier now sees (owner, 2026-09-28) — so the
        // preview shows them too. Same columns the supplier RPCs read.
        + 'payment_plan, payment_pct, delivery_preference, sample_requirement')
      .eq('id', requestId).maybeSingle(),
    sb.from('request_customization')
      .select('customization_types, customization_details, delivery_timeframe, ship_country, ship_city, additional_notes, attachment_paths')
      .eq('request_id', requestId).maybeSingle().then((r) => r, () => ({ data: null })),
    sb.from('request_factory_invites')
      .select('id, factory_id, slug, status, created_at, expires_at')
      .eq('request_id', requestId).order('created_at', { ascending: true })
      .then((r) => r, () => ({ data: [] })),
    sb.from('managed_request_briefs').select('supplier_brief, ai_output')
      .eq('request_id', requestId).maybeSingle().then((r) => r, () => ({ data: null })),
    // ⚠️ NO managed_visibility FILTER, deliberately. offers_select gives an
    // admin every offer on the request (migration 20260918172830, the admin
    // branch of the policy), and an offer HELD from the trader is precisely the
    // one staff need to see: it is waiting on somebody here approving the
    // supplier. Filtering to buyer_visible would hide the only rows that
    // require an action.
    //
    // visibility_reason is selected, not just managed_visibility, because the
    // two admin_only reasons mean opposite things and must never be rendered
    // alike — 'unverified_supplier' is a temporary hold that ends on approval,
    // 'managed_raw' is hidden from the trader permanently by design. Migration
    // 20260919120440 states that distinction and its count function excludes
    // managed_raw for the same reason.
    //
    // The supplier embed names the FK constraint, which is what PostgREST needs
    // and what AdminConciergeDetail.jsx:152 already proves resolves.
    sb.from('offers')
      .select('id, supplier_id, status, managed_visibility, visibility_reason, '
        + 'price, currency, moq, delivery_days, created_at, '
        // sar_rate: the rate locked at acceptance (fx_rate_lock). ⚠️ Needs that
        // migration applied first — before it, this column does not exist.
        + 'sar_rate, '
        // license_photo rides along so the screen can tell a supplier who has
        // FILED something from one who has not. bucketOf() needs both it and
        // status; without it every held offer would offer a review link,
        // including for suppliers with nothing to review.
        + 'supplier:profiles!offers_supplier_id_fkey(full_name, company_name, status, license_photo)')
      .eq('request_id', requestId).order('created_at', { ascending: true })
      .then((r) => r, () => ({ data: [] })),
    // The trader's text as typed, before contact details were masked at save
    // (20261011223741). Admin-only by RLS; empty when nothing was masked,
    // which is the usual case. A read failure is logged, not swallowed: an
    // empty list here would read as "nothing was hidden" (security rule 4).
    sb.from('request_text_originals')
      .select('field, original, updated_at')
      .eq('request_id', requestId)
      .then((r) => {
        if (r.error) console.error('[consoleRequestDetail] request_text_originals read failed:', r.error.message || r.error);
        return r;
      }, (e) => { console.error('[consoleRequestDetail] request_text_originals threw:', e?.message || e); return { data: [] }; }),
  ]);

  if (error) throw error;
  if (!req) return null;

  const c = cust?.data || null;
  const inviteRows = invites?.data || [];

  let buyer = null;
  if (req.buyer_id) {
    const { data } = await sb.from('profiles')
      .select('id, full_name, company_name, city, country, created_at')
      .eq('id', req.buyer_id).maybeSingle();
    buyer = data || null;
  }

  // Factory names for the invite list, so the admin sees who already has a link.
  let factories = {};
  const facIds = [...new Set(inviteRows.map((i) => i.factory_id).filter(Boolean))];
  if (facIds.length) {
    const { data } = await sb.from('factory_directory')
      .select('id, company_name, company_name_latin, linked_supplier_id')
      .in('id', facIds);
    for (const f of data || []) factories[f.id] = f;
  }

  // Same union get_factory_invite returns since migration 20260916094415: the
  // request's own column and the customization's, deduped. A path the factory
  // page lists and this screen does not would make the preview a lie in the one
  // direction that matters — the admin would not see what he is sending.
  const paths = [...new Set([
    ...(Array.isArray(req.attachment_paths) ? req.attachment_paths : []),
    ...(Array.isArray(c?.attachment_paths) ? c.attachment_paths : []),
  ].filter((p) => typeof p === 'string' && p !== ''))];

  // Every path signed, not just the first: this screen is where an admin checks
  // what the factory will actually receive, and a thumbnail of image one answers
  // nothing about image three. Ten-minute URLs, same as the factory page mints.
  const signed = await signRequestAttachments(paths);

  const all = brief?.data?.ai_output?.supplier_brief_all || {};

  // The request fee (2026-09-29): an unpaid fee makes the request not
  // offerable, exactly as the server's request_is_offerable() now says.
  const fee = await fetchRequestFee(sb, requestId);

  return {
    request: req,
    // Computed here, from the raw row, so the panels never have to work out
    // which shape of "request" they were handed. The quotations board passes a
    // row whose `status` is a DERIVED value (consoleQuotations.js), and a panel
    // testing that field greyed its buttons on live requests in production.
    offerable: isRequestOfferable(req) && isFeeSettled(fee),
    fee,
    customization: c,
    buyer,
    invites: inviteRows.map((i) => ({ ...i, factory: factories[i.factory_id] || null })),
    // Kept: callers already read it, and it still means "every offer on this
    // request" — the rows below are the same set, not a subset.
    offerCount: (offers?.data || []).length,
    offers: offers?.data || [],
    // [{ field, original, updated_at }] — see the read above.
    textOriginals: originals?.data || [],
    attachments: { paths, signed },
    // What the factory reads, in the order /f/ prefers it.
    factoryView: {
      title: nf(req.title_en) || nf(req.title_zh) || nf(req.title_ar),
      brief: nf(all.en) || nf(all.zh) || nf(brief?.data?.supplier_brief) || '',
      description: nf(req.description_en) || nf(req.description_zh) || nf(req.description_ar),
      quantity: req.quantity, unit: req.unit,
      customization_types: c?.customization_types || null,
      delivery_timeframe: c?.delivery_timeframe || null,
      ship_country: nf(req.ship_country) || nf(c?.ship_country),
      ship_city: nf(req.ship_city) || nf(c?.ship_city),
      deadline: req.response_deadline,
      // Rendered through src/lib/supplierRequestTerms.js, the builder every
      // supplier door uses, so the preview and the page cannot word them apart.
      budget_per_unit: req.budget_per_unit,
      budget_currency: req.budget_currency,
      payment_plan: req.payment_plan,
      payment_pct: req.payment_pct,
      delivery_preference: req.delivery_preference,
      sample_requirement: req.sample_requirement,
    },
  };
}
